
import { Component, Vue } from 'vue-property-decorator'
import * as XLSX from 'xlsx'
import DirectUpload from '@/components/DirectUpload/index.vue'
import { Program } from '@/models'
import { importCustomers } from '@/api/customers'
import {
  CustomerImportFail,
  CustomerImportResult,
  formatImportApiError,
  formatImportFailError,
  parseImportApiFails
} from '../utils/importErrors'

@Component({
  name: 'DialogImportCustomers',
  components: {
    DirectUpload
  }
})
export default class extends Vue {
  visible = false
  loading = false
  uploading = false
  programsLoading = false
  uploadKey = 0
  importResult: CustomerImportResult | null = null
  failsPage = 1
  readonly pageSize = 5

  programs: Program[] = []
  selectedProgramId: string | null = null
  pendingSignedId: string | null = null
  pendingFileName: string | null = null
  uploadingFileName: string | null = null

  get columns() {
    return [
      { key: 'firstname', label: this.$t('customers.import.columns.firstname'), required: true },
      { key: 'lastname', label: this.$t('customers.import.columns.lastname'), required: true },
      { key: 'email', label: this.$t('customers.import.columns.email'), required: true },
      { key: 'phone_number', label: this.$t('customers.import.columns.phone_number'), required: false }
    ]
  }

  get hasSingleProgram(): boolean {
    return this.programs.length === 1
  }

  get selectedProgramName(): string {
    const program = this.programs.find(item => item.id === this.selectedProgramId)
    return program?.name || ''
  }

  get canUpload(): boolean {
    return Boolean(this.selectedProgramId) && !this.programsLoading
  }

  get canSubmit(): boolean {
    return Boolean(this.selectedProgramId && this.pendingSignedId) && !this.isBusy
  }

  get isBusy(): boolean {
    return this.loading || this.uploading || this.programsLoading
  }

  get loadingText(): string {
    if (this.programsLoading) return String(this.$t('customers.import.loadingPrograms'))
    return String(this.$t('customers.import.loading'))
  }

  get successCount(): number {
    return this.importResult?.success?.length || 0
  }

  get failsCount(): number {
    return this.importResult?.fails?.length || 0
  }

  get paginatedFails(): CustomerImportFail[] {
    if (!this.importResult) return []
    const start = (this.failsPage - 1) * this.pageSize
    return this.importResult.fails.slice(start, start + this.pageSize)
  }

  get resultIcon(): string {
    if (this.successCount > 0 && this.failsCount === 0) return 'success'
    if (this.successCount > 0) return 'warning'
    return 'error'
  }

  get resultTitle(): string {
    if (this.successCount === 0 && this.failsCount > 0) {
      return String(this.$t('customers.import.resultErrorTitle'))
    }
    return String(this.$t('customers.import.resultTitle'))
  }

  get resultSubtitle(): string {
    if (this.successCount === 0 && this.failsCount > 0) {
      return String(this.$t('customers.import.resultErrorSubtitle', {
        fails: this.failsCount
      }))
    }
    return String(this.$t('customers.import.resultSubtitle', {
      success: this.successCount,
      fails: this.failsCount
    }))
  }

  setVisibility(value: boolean): void {
    this.visible = value
    if (value) {
      this.resetState()
      this.loadPrograms()
    }
  }

  handleClose(): void {
    this.visible = false
    this.resetState()
  }

  resetState(): void {
    this.loading = false
    this.uploading = false
    this.importResult = null
    this.failsPage = 1
    this.pendingSignedId = null
    this.pendingFileName = null
    this.uploadingFileName = null
    this.uploadKey += 1
  }

  resetForNewImport(): void {
    this.importResult = null
    this.failsPage = 1
    this.pendingSignedId = null
    this.pendingFileName = null
    this.uploading = false
    this.uploadingFileName = null
    this.uploadKey += 1
  }

  clearPendingFile(): void {
    this.pendingSignedId = null
    this.pendingFileName = null
    this.uploading = false
    this.uploadingFileName = null
    this.uploadKey += 1
  }

  private async loadPrograms(): Promise<void> {
    this.programsLoading = true
    this.programs = []
    this.selectedProgramId = null

    try {
      const { data } = await Program
        .page(1)
        .per(999)
        .all()

      this.programs = data
      if (this.programs.length === 1) {
        this.selectedProgramId = this.programs[0].id ?? null
      }
    } catch (error) {
      this.$notify({
        title: String(this.$t('customers.import.notification.errorTitle')),
        message: String(this.$t('customers.import.errors.loadPrograms')),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.programsLoading = false
    }
  }

  downloadTemplate(): void {
    const content = [
      'firstname;lastname;email;phone_number',
      'Jean;Dupont;jean.dupont@example.com;0612345678'
    ].join('\n')
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'customers_import_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  downloadExcelTemplate(): void {
    const rows = [
      ['firstname', 'lastname', 'email', 'phone_number'],
      ['Jean', 'Dupont', 'jean.dupont@example.com', '0612345678']
    ]
    const worksheet = XLSX.utils.aoa_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers')
    XLSX.writeFile(workbook, 'customers_import_template.xlsx')
  }

  formatFailError(error: string): string {
    return formatImportFailError(error, this.$t.bind(this) as any)
  }

  handleUploadStart(file: any): void {
    this.uploading = true
    this.uploadingFileName = file?.file?.name || null
    this.pendingSignedId = null
    this.pendingFileName = null
  }

  handleUploadError(error: string): void {
    this.uploading = false
    this.uploadingFileName = null
    this.$notify({
      title: String(this.$t('customers.import.notification.errorTitle')),
      message: error || String(this.$t('customers.import.errors.uploadFailed')),
      type: 'error',
      duration: 8000
    })
    this.clearPendingFile()
  }

  handleUpload(file: any): void {
    this.uploading = false
    this.uploadingFileName = null

    const signedId = file?.blob?.signed_id
    if (!signedId) {
      this.$notify({
        title: String(this.$t('customers.import.notification.errorTitle')),
        message: String(this.$t('customers.import.errors.uploadFailed')),
        type: 'error',
        duration: 5000
      })
      return
    }

    this.pendingSignedId = signedId
    this.pendingFileName = file?.blob?.filename || file?.file?.file?.name || 'import.csv'
  }

  private async runImport(signedId: string, programId: string, attempt = 1): Promise<any> {
    try {
      return await importCustomers(signedId, programId)
    } catch (err: any) {
      if (err?.response?.status === 409 && attempt < 2) {
        await new Promise(resolve => setTimeout(resolve, 1500))
        return this.runImport(signedId, programId, attempt + 1)
      }
      throw err
    }
  }

  async submitImport(): Promise<void> {
    if (!this.canSubmit || !this.selectedProgramId || !this.pendingSignedId) return

    this.loading = true
    try {
      const response: any = await this.runImport(this.pendingSignedId, this.selectedProgramId)
      const attributes = response?.data?.attributes || response?.attributes
      this.importResult = {
        success: attributes?.success || [],
        fails: attributes?.fails || []
      }
      this.failsPage = 1

      if (this.successCount > 0) {
        this.$emit('imported')
      }

      this.$notify({
        title: String(this.$t('customers.import.notification.doneTitle')),
        message: this.resultSubtitle,
        type: this.failsCount === 0 ? 'success' : 'warning',
        duration: 4000
      })
    } catch (err: any) {
      const parsedFails = parseImportApiFails(err)
      if (parsedFails.length > 0) {
        this.importResult = {
          success: [],
          fails: parsedFails
        }
        this.failsPage = 1
        this.$notify({
          title: String(this.$t('customers.import.notification.errorTitle')),
          message: this.resultSubtitle,
          type: 'error',
          duration: 5000
        })
      } else {
        const message = formatImportApiError(err, this.$t.bind(this) as any)
        this.$notify({
          title: String(this.$t('customers.import.notification.errorTitle')),
          message,
          type: 'error',
          duration: 8000,
          dangerouslyUseHTMLString: false
        })
        this.clearPendingFile()
      }
    } finally {
      this.loading = false
    }
  }
}
